import { useMemo, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { scheduleLessonSchema } from '@/constants/ValidationSchema';
import { CreateArrayOptions } from '@/constants/constant';
import { ServiceItem } from '@/constants/Interfaces';
import {
  useCreateNewLessonMutation,
  useDeleteLessonMutation,
  useGetHotelListQuery,
  useGetInstructorListQuery,
  useGetServiceListQuery,
  useUpdateLessonMutation,
  useUpdateProcessAndUnprocessedLessonsMutation
} from '@/services';
import { useGetCarsListQuery } from '@/services/transport.services';
import { useGetMountainListQuery } from '@/services/mountain.service';
import { toHHmm } from '@/utils/common';
import {
  addMinutes as addMinutesHelper,
  formatDateYmd,
  toMinutes as toMinutesHelper
} from './timeHelpers';
import { useAvailabilityChecks } from './useAvailabilityChecks';
import type { LessonDTO, LessonModalConfig, OptionItem } from './types';
import { useLessonClipboard } from './useLessonClipboard';
import { useLocalConfirmModal } from './useLocalConfirmModal';
import {
  useLessonClipboardHandlers,
  useLessonConfirmations
} from './useLessonClipboardConfirm';
import useDailyRosterStore from '@/stores/dailyRosterStore';
import useInstructorRosterStore from '@/stores/instructorRosterStore';
import toast from 'react-hot-toast';
import {
  useCheckLessonAvailabilityMutation,
  useCheckCarAvailabilityMutation
} from '@/services/lesson.service';
import CustomConfirmModal from '@/components/Common/Modals/CustomConfirmModal';

type ScheduleLessonFormData = z.infer<typeof scheduleLessonSchema>;

// Storage key for copy/paste clipboard
const LESSON_CLIPBOARD_KEY = 'hakuba:lesson_form_clipboard:v1';

export const useScheduleLessonForm = (
  modalConfig: LessonModalConfig,
  lesson: LessonDTO,
  isEditing: boolean,
  onClose?: () => void
) => {
  const modalData = modalConfig?.data as
    | import('./types').ModalConfigData
    | undefined;
  const { mutateAsync: createNewLesson } = useCreateNewLessonMutation();
  const { mutateAsync: updateLesson } = useUpdateLessonMutation();
  const { mutateAsync: deleteLesson } = useDeleteLessonMutation();
  const { mutateAsync: updateProcessAndUnprocessedLessons } =
    useUpdateProcessAndUnprocessedLessonsMutation();

  const {
    mutateAsync: checkLessonAvailabilityMutation,
    isPending: isCheckingLessonAvailability
  } = useCheckLessonAvailabilityMutation();

  const { mutateAsync: checkCarAvailabilityMutation } =
    useCheckCarAvailabilityMutation();

  // Fetch dropdown data
  const commonPayload = 'pagination=false&status=Active';
  const { data: instructorListData } = useGetInstructorListQuery(
    `${commonPayload}&sortBy=name&sortOrder=ASC`
  );
  const { data: serviceListData } = useGetServiceListQuery(commonPayload);
  const { data: hotelListData } = useGetHotelListQuery(
    `${commonPayload}&type=hotel&sortBy=name&sortOrder=ASC`
  );
  const { data: agentListData } = useGetHotelListQuery(
    `${commonPayload}&type=agent&sortBy=name&sortOrder=ASC`
  );
  const { data: carListData } = useGetCarsListQuery(commonPayload);
  const { data: mountainListData } = useGetMountainListQuery(commonPayload);

  const { lessonSchedule, setLessonSchedule } = useDailyRosterStore();
  const {
    lessonSchedule: instructorLessonSchedule,
    setLessonSchedule: setInstructorLessonSchedule
  } = useInstructorRosterStore();

  const { ConfirmModalUI, openConfirm } = useLocalConfirmModal();
  const { hasClipboard, copy, paste } =
    useLessonClipboard(LESSON_CLIPBOARD_KEY);

  // availability values are initialized after form/watch values

  // Duplicate booking modal handlers will be defined after availability hook

  // Service options logic
  type ServiceOptionSource = {
    id?: number;
    code?: string;
    hours?: number | string;
  };

  const availableServices = useMemo(() => {
    const svc = (lesson as { available_services?: ServiceOptionSource[] })
      ?.available_services;
    return Array.isArray(svc) ? svc : [];
  }, [lesson]);

  const serviceOptions = useMemo(() => {
    const mapped = availableServices.map(s => ({
      value: s?.id ?? 0,
      label: s?.code ?? String(s?.id ?? '')
    }));
    return mapped.length
      ? mapped
      : [
          { value: 1, label: 'KLPM' },
          { value: 2, label: 'PRIV' }
        ];
  }, [availableServices]);

  // Dropdown data processing
  const instructors = useMemo(() => {
    const items = ((instructorListData as { items?: OptionItem[] } | undefined)
      ?.items ?? []) as Array<{
      id: number;
      name: string;
    }>;
    return items.map(it => ({ value: it.id, label: it.name }));
  }, [instructorListData]);

  const services = useMemo(() => {
    const items = ((serviceListData as { items?: ServiceItem[] } | undefined)
      ?.items ?? []) as ServiceItem[];
    return items;
  }, [serviceListData]);

  const hotels = useMemo(() => {
    const items = ((hotelListData as { items?: any[] } | undefined)?.items ??
      []) as Array<{
      id: number;
      name: string;
      google_map_link?: string | null;
    }>;
    return [
      { value: '', label: 'Select hotel', google_map_link: '' },
      ...items.map(it => ({
        value: it.id,
        label: it.name,
        google_map_link: it.google_map_link ?? undefined
      }))
    ];
  }, [hotelListData]);

  const agent = useMemo(() => {
    const items = ((agentListData as { items?: OptionItem[] } | undefined)
      ?.items ?? []) as Array<{
      id: number;
      name: string;
    }>;
    return [
      { value: '', label: 'Select agent' },
      ...items.map(it => ({ value: it.id, label: it.name }))
    ];
  }, [agentListData]);

  const cars = useMemo(() => {
    const items = ((carListData as { items?: OptionItem[] } | undefined)
      ?.items ?? []) as Array<{
      id: number;
      name: string;
    }>;
    return [
      { value: '', label: 'Select vehicle' },
      ...items.map(it => ({ value: it.id, label: it.name }))
    ];
  }, [carListData]);

  const mountains = useMemo(() => {
    const items = ((mountainListData as { items?: OptionItem[] } | undefined)
      ?.items ?? []) as Array<{
      id: number;
      name: string;
      default?: number;
    }>;
    return [
      { value: '', label: 'Select mountain' },
      ...items.map(it => ({ value: it.id, label: it.name }))
    ];
  }, [mountainListData]);

  // Resolve default mountain id where item's default === 1
  const defaultMountainId = useMemo(() => {
    const items = ((mountainListData as { items?: OptionItem[] } | undefined)
      ?.items ?? []) as Array<{
      id: number;
      name: string;
      default?: number;
    }>;
    const found = items.find(it => Number(it?.default) === 1);
    return found?.id as number | undefined;
  }, [mountainListData]);

  const serviceOptionsFromQuery = useMemo(() => {
    const items = services.length ? services : [];
    if (items.length) {
      return items.map(s => ({ value: s.id, label: s.code }));
    }
    return serviceOptions;
  }, [services, serviceOptions]);

  // Initial date and default values
  const initialDate = useMemo(() => {
    const raw = modalConfig?.data?.date;
    if (raw instanceof Date) return raw;
    if (typeof raw === 'string') {
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date();
  }, [modalConfig?.data]);

  const defaultValues = useMemo<Partial<ScheduleLessonFormData>>(
    () => ({
      date: initialDate,
      instructor_id: modalData?.instructor_id ?? undefined,
      string:
        lesson?.string !== undefined && lesson?.string !== null
          ? Number(lesson?.string)
          : (undefined as unknown as number),
      client_name: isEditing ? (lesson?.client_name ?? '') : 'Group',
      lesson_code_id: lesson?.lesson_code?.id ?? undefined,
      am: lesson?.am ?? 0,
      pm: lesson?.pm ?? 0,
      fd: lesson?.fd ?? 0,
      ski_level: lesson?.ski_level ?? '',
      start: isEditing
        ? toHHmm(lesson?.start as string)
        : String(modalData?.slot ?? ''),
      end: isEditing ? toHHmm(lesson?.end as string) : '',
      hotel_id: (lesson as any)?.hotel_id ?? lesson?.hotel?.id,
      car_id:
        (lesson as any)?.car_id ??
        lesson?.car?.id ??
        lesson?.vehicle?.id ??
        lesson?.transport?.id ??
        lesson?.transports?.id,
      pickup_time: toHHmm(lesson?.pickup_time as string) ?? '',
      mountain_id: (lesson as any)?.mountain_id ?? lesson?.mountain?.id,
      agent_id: (lesson as any)?.agent_id ?? lesson?.agent?.id,
      lesson_req: (lesson as any)?.lesson_req ?? 'NA',
      reference: lesson?.reference ?? '',
      paid: (() => {
        const raw = lesson?.paid;
        if (raw === 'Y' || raw === 'N') return raw as 'Y' | 'N';
        if (typeof raw === 'boolean') return raw ? 'Y' : 'N';
        return 'N';
      })(),
      comments: lesson?.comments ?? '',
      transport_pax: lesson?.transport_pax ?? 0
    }),
    [lesson, initialDate, isEditing, modalConfig?.data]
  );

  // Form setup
  const form = useForm<ScheduleLessonFormData>({
    resolver: zodResolver(scheduleLessonSchema),
    defaultValues,
    mode: 'onChange'
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    watch,
    setValue,
    getValues,
    reset
  } = form;

  // Watched values
  const selectedStart = watch('start');
  const selectedEnd = watch('end');
  const selectedInstructorId = watch('instructor_id');
  const selectedDate = watch('date');
  const selectedPickupTime = watch('pickup_time');
  const selectedCarId = watch('car_id');
  const selectedServiceId = watch('lesson_code_id');
  const selectedMountainId = watch('mountain_id');
  const selectedSkiLevel = watch('ski_level');

  // Auto-select default mountain if none selected
  useEffect(() => {
    if (!selectedMountainId && defaultMountainId) {
      setValue('mountain_id', defaultMountainId, { shouldValidate: true });
    }
  }, [selectedMountainId, defaultMountainId, setValue]);

  const isPaidInitial = useMemo(() => {
    const lessonsEntry = (lesson as any)?.lessons_entry;
    if (lessonsEntry === 'Y') return true;
    if (lessonsEntry === 'N' || lessonsEntry == null) return false;
    return false;
  }, [lesson]);

  // Utility functions
  const getServiceDurationMinutes = useCallback(
    (serviceId?: number) => {
      if (!serviceId) return 60;
      const svc = services.find(s => s.id === serviceId);
      const hours =
        typeof svc?.hours === 'string' ? parseFloat(svc.hours) : svc?.hours;
      return Math.max(15, Math.round((hours ?? 1) * 60));
    },
    [services]
  );

  const addMinutes = addMinutesHelper;

  const toMinutes = useCallback((time?: string) => toMinutesHelper(time), []);

  const formatDate = formatDateYmd;

  // Availability effects moved into useAvailabilityChecks (call after watch values)
  const {
    lessonAvailability,
    isCarAvailable,
    showDuplicateBookingModal,
    setShowDuplicateBookingModal,
    showDuplicateVehicleBookingModal,
    setShowDuplicateVehicleBookingModal
  } = useAvailabilityChecks(
    {
      selectedDate,
      selectedStart,
      selectedEnd,
      selectedInstructorId,
      lessonId: (modalConfig?.data as any)?.lesson?.id as number | undefined
    },
    {
      selectedDate,
      selectedPickupTime,
      selectedCarId,
      scheduleId: (modalConfig?.data as any)?.lesson?.id as number | undefined,
      defaultCarId: defaultValues.car_id,
      defaultPickupTime: defaultValues.pickup_time
    },
    (query: string) => checkLessonAvailabilityMutation(query),
    (query: string) => checkCarAvailabilityMutation(query),
    formatDate
  );

  // Duplicate booking modal handlers (after availability)
  const handleDuplicateBookingContinue = useCallback(() => {
    setShowDuplicateBookingModal(false);
    setShowDuplicateVehicleBookingModal(false);
  }, [setShowDuplicateBookingModal, setShowDuplicateVehicleBookingModal]);

  // Duplicate booking modal UI (after availability values exist)
  const DuplicateBookingModalUI = (
    <CustomConfirmModal
      show={showDuplicateBookingModal || showDuplicateVehicleBookingModal}
      setShow={
        showDuplicateBookingModal
          ? setShowDuplicateBookingModal
          : setShowDuplicateVehicleBookingModal
      }
      data={{
        title: showDuplicateBookingModal
          ? 'Duplicate booking alert!'
          : 'Duplicate vehicle booking alert!',
        message: showDuplicateBookingModal
          ? 'Time slot is already booked for another booking. Please choose another slot or continue'
          : 'Time slot is already booked for selected vehicle. Please choose another vehicle or continue',
        onClick: handleDuplicateBookingContinue
      }}
    />
  );

  // Effects
  useEffect(() => {
    // Prefer explicit instructor_id passed via modal data
    const passedId = modalData?.instructor_id as number | undefined;
    if (passedId) {
      setValue('instructor_id', passedId, { shouldValidate: true });
      return;
    }

    // Fallback: match by instructor name when available
    const targetName = (modalData?.instructor?.name ||
      lesson?.instructor?.name) as string | undefined;
    if (!targetName || !instructors?.length) return;
    const match = instructors.find(opt => opt.label === targetName);
    if (match) {
      setValue('instructor_id', match.value as number, {
        shouldValidate: true
      });
    }
  }, [instructors, modalConfig?.data, lesson, setValue]);

  // Compute read-only end time on start/service change
  useEffect(() => {
    if (!selectedStart) return;
    const initialStart = defaultValues.start as string | undefined;
    const initialService = defaultValues.lesson_code_id as number | undefined;
    const hasChanged =
      selectedStart !== (initialStart ?? '') ||
      selectedServiceId !== (initialService ?? undefined);
    if (isEditing && (lesson as any)?.end && !hasChanged) return;
    const minutes: any = getServiceDurationMinutes(selectedServiceId);
    setValue('end', addMinutes(selectedStart, minutes), {
      shouldValidate: true
    });
  }, [
    selectedStart,
    selectedServiceId,
    getServiceDurationMinutes,
    setValue,
    isEditing,
    lesson,
    defaultValues,
    addMinutes
  ]);

  // Handle pickup time validation
  useEffect(() => {
    if (!selectedStart || !selectedPickupTime) return;
    if (toMinutes(selectedPickupTime) > toMinutes(selectedStart)) {
      setValue('pickup_time', '', { shouldValidate: true });
    }
  }, [selectedStart, selectedPickupTime, toMinutes, setValue]);

  // Form submission
  const onSubmit = async (data: ScheduleLessonFormData) => {
    // Validation: block group services for SK6/SB6 levels
    try {
      const selectedService = services.find(
        s => s.id === Number(data.lesson_code_id)
      );
      const serviceMode = (selectedService?.service_mode || '')
        .toString()
        .toLowerCase();
      const ski = (data.ski_level || '').toString().toUpperCase();
      if (serviceMode === 'groups' && (ski === 'SK6' || ski === 'SB6')) {
        toast.error('Group services cannot be booked for SK6 or SB6 levels.');
        return false;
      }
    } catch (err) {
      // ignore and continue
    }
    const payload = {
      date: formatDate(data.date),
      instructor_id: Number(data.instructor_id),
      string: data?.string ? data?.string?.toString() : '',
      client_name: data.client_name,
      lesson_code_id: Number(data.lesson_code_id),
      am: typeof data.am === 'number' ? data.am : 0,
      pm: typeof data.pm === 'number' ? data.pm : 0,
      fd: typeof data.fd === 'number' ? data.fd : 0,
      ski_level: data.ski_level,
      start: data.start,
      end: data.end,
      hotel_id: typeof data.hotel_id === 'number' ? data.hotel_id : null,
      car_id: typeof data.car_id === 'number' ? data.car_id : null,
      pickup_time: data.pickup_time || '',
      mountain_id:
        typeof data.mountain_id === 'number' ? data.mountain_id : null,
      agent_id: typeof data.agent_id === 'number' ? data.agent_id : null,
      reference: data.reference || '',
      paid: data.paid === 'Y' ? 'Y' : 'N',
      comments: data.comments || '',
      transport_pax: data?.transport_pax || 0,
      lesson_req: data.lesson_req || 'NA'
    };
    try {
      if (isEditing) {
        await updateLesson({
          ...payload,
          id: modalData?.lessonId ?? 0
        });

        toast.success('Lesson updated successfully');
      } else {
        await createNewLesson({
          ...payload
        });
        toast.success('Lesson added successfully');
      }
      modalConfig?.onClick?.();
      return true;
    } catch {
      return false;
    }
  };

  // Invalid combination flag: service_mode 'groups' with SK6/SB6
  const invalidCombination = useMemo(() => {
    try {
      const svc = services.find(s => s.id === Number(selectedServiceId));
      const serviceMode = (svc?.service_mode || '').toString().toLowerCase();
      const ski = (selectedSkiLevel || '').toString().toUpperCase();
      return serviceMode === 'groups' && (ski === 'SK6' || ski === 'SB6');
    } catch {
      return false;
    }
  }, [services, selectedServiceId, selectedSkiLevel]);

  const { handleCopyForm, handlePasteForm } = useLessonClipboardHandlers(
    getValues,
    reset,
    copy,
    paste,
    getServiceDurationMinutes,
    addMinutes,
    watch,
    setValue,
    toast
  );

  // Confirmation handlers
  const { confirmCopyForm, confirmPasteForm } = useLessonConfirmations(
    openConfirm,
    toast,
    deleteLesson,
    updateProcessAndUnprocessedLessons,
    lessonSchedule,
    setLessonSchedule,
    modalConfig,
    onClose
  );

  // Expose zero-arg wrappers to keep API unchanged
  const wrappedConfirmCopyForm = useCallback(() => {
    return confirmCopyForm(handleCopyForm);
  }, [confirmCopyForm, handleCopyForm]);

  const wrappedConfirmPasteForm = useCallback(() => {
    return confirmPasteForm(handlePasteForm, hasClipboard);
  }, [confirmPasteForm, handlePasteForm, hasClipboard]);

  const confirmDeleteLesson = useCallback(async () => {
    const lessonId = (modalConfig?.data?.lesson as any)?.id as
      | number
      | undefined;
    const instructorId = (modalConfig?.data?.instructor as any)?.id as
      | number
      | undefined;
    if (!lessonId || !instructorId) return;
    openConfirm(
      'Delete Lesson',
      'Are you sure you want to delete this lesson?',
      async () => {
        const resp = await deleteLesson(lessonId);
        if (resp?.success) {
          modalConfig?.onClick?.();
          // Defer closing to ensure state updates apply cleanly
          if (onClose) setTimeout(() => onClose(), 0);
        }
      }
    );
  }, [
    deleteLesson,
    modalConfig?.data,
    openConfirm,
    modalConfig?.onClick,
    lessonSchedule,
    setLessonSchedule,
    onClose
  ]);

  const confirmProcessAndUnprocessedLessons = useCallback(() => {
    const scheduleId = modalConfig?.data?.lessonId as number | undefined;
    const instructorId = (modalConfig?.data?.instructor as any)?.id as number;
    if (!scheduleId) return;
    openConfirm(
      isPaidInitial ? 'Move as Unprocessed' : 'Move as Processed',
      isPaidInitial
        ? 'Are you sure you want to move this lesson as Unprocessed?'
        : 'Are you sure you want to move this lesson as Processed?',
      async () => {
        try {
          const resp = await updateProcessAndUnprocessedLessons({
            id: scheduleId
          });
          if ((resp as any)?.success) {
            const newPaidStatus = isPaidInitial ? 'N' : 'Y';

            const updateScheduleData = (currentSchedule: any[]) => {
              if (
                !Array.isArray(currentSchedule) ||
                currentSchedule.length === 0
              ) {
                return currentSchedule;
              }

              const isBucketed =
                'lessons' in currentSchedule[0] &&
                Array.isArray(currentSchedule[0].lessons);

              if (isBucketed) {
                return currentSchedule.map(data => {
                  if (String(data?.id) === String(instructorId)) {
                    const lessons = (data?.lessons || []) as any[];
                    const updatedLessons = lessons.map(l => {
                      if (
                        String(l?.schedule_group?.id) === String(scheduleId)
                      ) {
                        return { ...l, paid: newPaidStatus };
                      }
                      return l;
                    });
                    return { ...data, lessons: updatedLessons };
                  }
                  return data;
                });
              } else {
                return currentSchedule.map(l => {
                  if (String(l?.schedule_group?.id) === String(scheduleId)) {
                    return { ...l, paid: newPaidStatus };
                  }
                  return l;
                });
              }
            };

            setLessonSchedule(prev => updateScheduleData(prev));
            setInstructorLessonSchedule(prev => updateScheduleData(prev));

            modalConfig?.onClick?.();
            if (onClose) onClose();
          }
        } catch {
          // Keep main modal open on error
        }
      }
    );
  }, [
    isPaidInitial,
    modalConfig?.data,
    modalConfig?.onClick,
    openConfirm,
    updateProcessAndUnprocessedLessons,
    lessonSchedule,
    setLessonSchedule,
    instructorLessonSchedule,
    setInstructorLessonSchedule,
    onClose
  ]);

  return {
    // Form
    form,
    control,
    handleSubmit,
    isSubmitting,
    watch,
    setValue,
    getValues,
    reset,

    // Data
    instructors,
    services,
    serviceOptionsFromQuery,
    hotels,
    agent,
    cars,
    mountains,
    CreateArrayOptions,

    // State
    isPaidInitial,
    isCarAvailable,
    selectedStart,
    selectedServiceId,
    selectedPickupTime,
    selectedCarId,

    // Availability
    isCheckingLessonAvailability,
    lessonAvailability,

    // Functions
    onSubmit,
    getServiceDurationMinutes,
    addMinutes,
    toMinutes,

    // Clipboard
    hasClipboard,
    confirmCopyForm: wrappedConfirmCopyForm,
    confirmPasteForm: wrappedConfirmPasteForm,

    // Confirmations
    ConfirmModalUI,
    DuplicateBookingModalUI,
    confirmDeleteLesson,
    confirmProcessAndUnprocessedLessons,
    invalidCombination,
    lessonsEntry: (lesson as any)?.lessons_entry
  };
};
